import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpDown, CheckCircle2, ChevronDown, ClipboardList, Clock, CopyPlus, Download, Link, Loader2, Package, Pencil, Percent, Plus, Search, ShieldAlert, Tags, Trash2, Upload } from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { useTreatmentCatalogConfig } from '../../lib/treatmentCatalog';
import { useI18n } from '../../i18n/LanguageContext';

type Scope = 'organization' | 'system';
type StockDeductionMode = 'AUTO_LOAD' | 'MANUAL_LOAD';

type BillingServiceRow = {
  id: string;
  code: string;
  appointmentTypeId?: string | null;
  appointmentTypeName?: string | null;
  name: string;
  description?: string;
  price: number;
  category?: string;
  stockRecipe: ServiceProductRecipeRow[];
  stockDeductionMode: StockDeductionMode;
  active: boolean;
  scope: Scope;
  bookingLink?: string;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  iscCode?: string;
  onlineBookable?: boolean;
  existingClientsOnly?: boolean;
  taxRate?: number;
  extraTimeMinutes?: number;
  serviceChargeAmount?: number;
  serviceGroup?: string;
  bundleName?: string;
  packageWidgetEnabled?: boolean;
};

type BillingProductRow = {
  id: string;
  code: string;
  name: string;
  description?: string;
  price: number;
  inventory: number;
  active: boolean;
  scope: Scope;
};

type CatalogResponse = {
  services: BillingServiceRow[];
  products?: BillingProductRow[];
};

type InventoryItemOption = {
  id: string;
  name: string;
  baseUnit: string;
  baseUnitId?: string | null;
  defaultConsumptionUnitId?: string | null;
  category?: string | null;
  strength?: string | null;
  packageSize?: string | null;
  trackLots?: boolean;
  lotId?: string | null;
  lotNumber?: string | null;
  available?: number;
};

type InventoryItemUnitOption = {
  unitId: string;
  name: string;
  abbreviation?: string | null;
  factorToBaseUnit: number;
  allowedForConsumption: boolean;
  active: boolean;
};

type AppointmentTypeOption = {
  id: string;
  name: string;
};

type ServiceProductRecipeRow = {
  billingProductId?: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unit?: string;
  unitId?: string;
  lotId?: string;
  lotNumber?: string;
  notes?: string;
};

const starterServices = [
  { code: 'CONSULT-INITIAL', name: 'Initial Consultation', description: 'Comprehensive first visit assessment', category: 'Consultation', price: 180 },
  { code: 'CONSULT-FOLLOWUP', name: 'Follow-up Consultation', description: 'Established patient follow-up visit', category: 'Consultation', price: 110 },
  { code: 'NURSING-INTAKE', name: 'Nursing Intake Assessment', description: 'Vitals and nursing intake before provider exam', category: 'Nursing', price: 35 },
  { code: 'PROCEDURE-MINOR', name: 'Minor Procedure', description: 'Office-based minor procedure/intervention', category: 'Procedure', price: 220 },
];

function normalizeServiceRecipe(value: unknown): ServiceProductRecipeRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item: any) => ({
      billingProductId: typeof item?.billingProductId === 'string' ? item.billingProductId : undefined,
      itemId: typeof item?.itemId === 'string' ? item.itemId : '',
      itemName: String(item?.itemName || '').trim(),
      quantity: Number(item?.quantity ?? item?.actualQuantity ?? item?.plannedQuantity ?? 1),
      unit: typeof item?.unit === 'string' ? item.unit : '',
      unitId: typeof item?.unitId === 'string' ? item.unitId : undefined,
      lotId: typeof item?.lotId === 'string' ? item.lotId : undefined,
      lotNumber: typeof item?.lotNumber === 'string' ? item.lotNumber : undefined,
      notes: typeof item?.notes === 'string' ? item.notes : '',
    }))
    .filter((item) => item.itemId && item.itemName && Number.isFinite(item.quantity) && item.quantity > 0);
}

function uniqueInventoryItems(rows: InventoryItemOption[]) {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (!row.id || seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
}

function inventoryLabel(item: InventoryItemOption) {
  return item.name;
}

export default function SettingsBillingCatalog() {
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { serviceVariantsEnabled } = useTreatmentCatalogConfig();
  const { language, tr } = useI18n();
  const tb = (phrase: string) => language === 'it'
    ? ({
        'Billables Catalog': 'Catalogo fatturabili',
        'Configure procedures, services, and products used by invoices across your clinic.':
          'Configura procedure, servizi e prodotti usati dalle fatture in tutta la clinica.',
        'Loading catalog...': 'Caricamento catalogo...',
      } as Record<string, string>)[phrase] || tr(phrase)
    : tr(phrase);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [services, setServices] = useState<BillingServiceRow[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItemOption[]>([]);
  const [inventoryItemUnits, setInventoryItemUnits] = useState<Record<string, InventoryItemUnitOption[]>>({});
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceForm, setServiceForm] = useState<Partial<BillingServiceRow>>({ active: true });
  const [serviceModalError, setServiceModalError] = useState<string | null>(null);
  const [serviceRecipeError, setServiceRecipeError] = useState<string | null>(null);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeOption[]>([]);
  const [bulkOnlineBookable, setBulkOnlineBookable] = useState(true);
  const [serviceChargeEnabled, setServiceChargeEnabled] = useState(true);
  const [packages, setPackages] = useState([
    { name: 'Consultation bundle', sessions: 3, price: 299, onlineWidget: true },
    { name: 'Skin care course', sessions: 6, price: 899, onlineWidget: false },
  ]);

  function parseCsvRows(csvText: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let i = 0;
    let inQuotes = false;

    while (i < csvText.length) {
      const char = csvText[i];
      const next = csvText[i + 1];

      if (inQuotes) {
        if (char === '"' && next === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        if (char === '"') {
          inQuotes = false;
          i += 1;
          continue;
        }
        cell += char;
        i += 1;
        continue;
      }

      if (char === '"') {
        inQuotes = true;
        i += 1;
        continue;
      }
      if (char === ',') {
        row.push(cell.trim());
        cell = '';
        i += 1;
        continue;
      }
      if (char === '\n') {
        row.push(cell.trim());
        rows.push(row);
        row = [];
        cell = '';
        i += 1;
        continue;
      }
      if (char === '\r') {
        i += 1;
        continue;
      }

      cell += char;
      i += 1;
    }

    if (cell.length > 0 || row.length > 0) {
      row.push(cell.trim());
      rows.push(row);
    }

    return rows.filter((r) => r.some((col) => col.length > 0));
  }

  function normalizeHeader(value: string) {
    return value.trim().toLowerCase().replace(/\s+/g, '_');
  }

  function toBoolean(value: string | undefined, defaultValue: boolean) {
    if (!value || !value.trim()) return defaultValue;
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    return defaultValue;
  }

  async function onImportCsvFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.currentTarget.value = '';
    if (!file) return;

    setSaving(true);
    setFailure(null);
    setBanner(null);

    try {
      const text = await file.text();
      const rows = parseCsvRows(text);
      if (rows.length < 2) {
        throw new Error('CSV must include a header row and at least one data row.');
      }

      const headers = rows[0].map(normalizeHeader);
      const dataRows = rows.slice(1);
      let created = 0;
      let failed = 0;
      const errors: string[] = [];

      for (let index = 0; index < dataRows.length; index += 1) {
        const row = dataRows[index];
        const rowNumber = index + 2;
        const values: Record<string, string> = {};
        headers.forEach((key, colIndex) => {
          values[key] = (row[colIndex] || '').trim();
        });

        const code = values.code;
        const name = values.name;
        const description = values.description || undefined;
        const priceRaw = values.price || values.unit_price || values.base_price || '0';
        const price = Number(priceRaw);

        if (!code || !name) {
          failed += 1;
          errors.push(`Row ${rowNumber}: code and name are required.`);
          continue;
        }
        if (!Number.isFinite(price) || price < 0) {
          failed += 1;
          errors.push(`Row ${rowNumber}: invalid price "${priceRaw}".`);
          continue;
        }

        const response = await fetchApi('/billing/catalog/services', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            name,
            description,
            category: values.category || undefined,
            price,
            active: toBoolean(values.active, true),
          }),
        });

        if (response.ok) {
          created += 1;
          continue;
        }

        failed += 1;
        errors.push(`Row ${rowNumber}: API rejected (${response.status}).`);
      }

      await loadCatalog();
      setBanner(`CSV import finished. Created ${created} item(s).${failed ? ` Failed ${failed} row(s).` : ''}`);
      if (failed) {
        setFailure(errors.slice(0, 4).join(' | ') + (errors.length > 4 ? ' ...' : ''));
      }
    } catch (err: any) {
      setFailure(err.message || 'Failed to import CSV.');
    } finally {
      setSaving(false);
    }
  }

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const data = await fetchJson<CatalogResponse>(`/billing/catalog?includeInactive=${includeInactive ? 'true' : 'false'}`);
      setServices((data.services || []).map((service) => ({ ...service, stockRecipe: normalizeServiceRecipe(service.stockRecipe) })));
    } catch (err: any) {
      setFailure(err.message || 'Unable to load catalog.');
    } finally {
      setLoading(false);
    }
  }, [fetchJson, includeInactive]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const loadInventoryItems = useCallback(async () => {
    try {
      const data = await fetchJson<{ data: InventoryItemOption[] }>('/inventory/summary?includeUnstocked=true');
      setInventoryItems(uniqueInventoryItems(data.data || []));
    } catch {
      setInventoryItems([]);
    }
  }, [fetchJson]);

  const loadInventoryItemUnits = useCallback(async (itemId: string) => {
    if (!itemId || inventoryItemUnits[itemId]) return;
    try {
      const data = await fetchJson<{ data: InventoryItemUnitOption[] }>(`/inventory/items/${itemId}/units`);
      setInventoryItemUnits((current) => ({
        ...current,
        [itemId]: Array.isArray(data.data) ? data.data.filter((unit) => unit.active && unit.allowedForConsumption) : [],
      }));
    } catch {
      setInventoryItemUnits((current) => ({ ...current, [itemId]: [] }));
    }
  }, [fetchJson, inventoryItemUnits]);

  useEffect(() => {
    void loadInventoryItems();
  }, [loadInventoryItems]);

  const loadAppointmentTypes = useCallback(async () => {
    if (!serviceVariantsEnabled) {
      setAppointmentTypes([]);
      return;
    }
    try {
      const data = await fetchJson<Array<{ id: string; name: string }>>('/appointment-types');
      setAppointmentTypes(Array.isArray(data) ? data.map((row) => ({ id: row.id, name: row.name })) : []);
    } catch {
      setAppointmentTypes([]);
    }
  }, [fetchJson, serviceVariantsEnabled]);

  useEffect(() => {
    void loadAppointmentTypes();
  }, [loadAppointmentTypes]);

  const filteredServices = useMemo(() => {
    const q = search.toLowerCase();
    return services.filter((s) => {
      if (!q) return true;
      return (
        s.code.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        (s.category || '').toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q)
      );
    });
  }, [services, search]);

  const serviceCategories = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    services.forEach((service) => {
      const category = String(service.category || '').trim();
      if (!category) return;
      const normalizedCategory = category.toLowerCase();
      const existing = counts.get(normalizedCategory);
      if (existing) {
        existing.count += 1;
        return;
      }
      counts.set(normalizedCategory, { name: category, count: 1 });
    });
    return Array.from(counts.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [services]);

  const serviceCategoryOptions = useMemo(
    () => serviceCategories.map((category) => category.name),
    [serviceCategories],
  );
  const serviceRecipeRows = useMemo(() => normalizeServiceRecipe(serviceForm.stockRecipe), [serviceForm.stockRecipe]);

  useEffect(() => {
    serviceRecipeRows.forEach((row) => {
      if (row.itemId && !inventoryItemUnits[row.itemId]) {
        void loadInventoryItemUnits(row.itemId);
      }
    });
  }, [inventoryItemUnits, loadInventoryItemUnits, serviceRecipeRows]);

  function openServiceModal(row?: BillingServiceRow) {
    setServiceForm(
      row
        ? { ...row, stockRecipe: normalizeServiceRecipe(row.stockRecipe) }
        : {
            code: '',
          appointmentTypeId: '',
            name: '',
            description: '',
            category: '',
            price: 0,
            stockRecipe: [],
            stockDeductionMode: 'AUTO_LOAD',
            active: true,
            bookingLink: '',
            bufferBeforeMinutes: 0,
            bufferAfterMinutes: 0,
            iscCode: '',
            onlineBookable: true,
            existingClientsOnly: false,
            taxRate: 0,
            extraTimeMinutes: 0,
            serviceChargeAmount: 0,
            serviceGroup: '',
            bundleName: '',
            packageWidgetEnabled: false,
          },
    );
    setShowServiceModal(true);
    setServiceModalError(null);
    setServiceRecipeError(null);
    setFailure(null);
  }

  function closeServiceModal() {
    setShowServiceModal(false);
    setServiceModalError(null);
    setServiceRecipeError(null);
  }

  function findClinicServiceCopy(row: BillingServiceRow) {
    return services.find((service) => service.scope === 'organization' && service.code === row.code);
  }

  function updateServiceRecipe(index: number, patch: Partial<ServiceProductRecipeRow>) {
    setServiceForm((current) => {
      const rows = normalizeServiceRecipe(current.stockRecipe);
      return { ...current, stockRecipe: rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)) };
    });
  }

  function getDefaultUnit(item: InventoryItemOption | undefined, units: InventoryItemUnitOption[]) {
    if (!item) return { unit: '', unitId: undefined as string | undefined };
    const preferred = units.find((unit) => unit.unitId === item.defaultConsumptionUnitId)
      || units.find((unit) => unit.unitId === item.baseUnitId)
      || units[0];
    return {
      unit: preferred?.abbreviation || preferred?.name || item.baseUnit || '',
      unitId: preferred?.unitId || item.defaultConsumptionUnitId || item.baseUnitId || undefined,
    };
  }

  function lotOptionsForItem(itemId: string) {
    const seen = new Set<string>();
    return inventoryItems
      .filter((item) => item.id === itemId && item.lotId && item.lotNumber)
      .filter((item) => {
        const key = `${item.lotId}::${item.lotNumber}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: item.lotId || '',
        label: item.lotNumber || 'Unnamed lot',
        description: typeof item.available === 'number'
          ? `Available ${item.available}${item.baseUnit ? ` ${item.baseUnit}` : ''}`
          : undefined,
      }));
  }

  function addServiceRecipeRow() {
    const item = inventoryItems[0];
    if (!item) {
      setServiceRecipeError('Create products in Inventory before associating them to a service.');
      return;
    }
    setServiceRecipeError(null);
    const units = inventoryItemUnits[item.id] || [];
    const defaultUnit = getDefaultUnit(item, units);
    if (!inventoryItemUnits[item.id]) {
      void loadInventoryItemUnits(item.id);
    }
    setServiceForm((current) => ({
      ...current,
      stockRecipe: [
        ...normalizeServiceRecipe(current.stockRecipe),
        {
          itemId: item.id,
          itemName: item.name,
          quantity: 1,
          unit: defaultUnit.unit,
          unitId: defaultUnit.unitId,
          lotId: undefined,
          lotNumber: undefined,
          notes: '',
        },
      ],
    }));
  }

  function selectRecipeInventoryProduct(index: number, value: string) {
    const item = inventoryItems.find((row) => row.id === value);
    const current = serviceRecipeRows;
    const currentRow = current[index];
    const units = item ? (inventoryItemUnits[item.id] || []) : [];
    const currentUnitStillValid = Boolean(currentRow?.unitId) && units.some((unit) => unit.unitId === currentRow.unitId);
    const defaultUnit = currentUnitStillValid
      ? { unit: currentRow?.unit || '', unitId: currentRow?.unitId }
      : getDefaultUnit(item, units);
    if (item && !inventoryItemUnits[item.id]) {
      void loadInventoryItemUnits(item.id);
    }
    updateServiceRecipe(index, {
      itemId: item?.id || '',
      itemName: item?.name || '',
      unit: defaultUnit.unit,
      unitId: defaultUnit.unitId,
      lotId: undefined,
      lotNumber: undefined,
    });
  }

  function selectRecipeLot(index: number, lotId: string) {
    const current = serviceRecipeRows[index];
    const item = inventoryItems.find((row) => row.id === current?.itemId);
    const lot = inventoryItems.find((row) => row.id === current?.itemId && row.lotId === lotId);
    updateServiceRecipe(index, {
      lotId: lotId || undefined,
      lotNumber: lotId ? (lot?.lotNumber || '') : undefined,
      notes: current?.notes || '',
      itemName: item?.name || current?.itemName || '',
    });
  }

  function selectRecipeUnit(index: number, unitId: string) {
    const row = serviceRecipeRows[index];
    const units = inventoryItemUnits[row?.itemId || ''] || [];
    const unit = units.find((entry) => entry.unitId === unitId);
    updateServiceRecipe(index, {
      unitId: unitId || undefined,
      unit: unit?.abbreviation || unit?.name || row?.unit || '',
    });
  }

  function removeServiceRecipeRow(index: number) {
    setServiceForm((current) => ({
      ...current,
      stockRecipe: normalizeServiceRecipe(current.stockRecipe).filter((_, rowIndex) => rowIndex !== index),
    }));
  }

  async function saveService(e: React.FormEvent) {
    e.preventDefault();
    if (!serviceForm.code?.trim() || !serviceForm.name?.trim()) {
      setServiceModalError('Code and name are required for services/procedures.');
      return;
    }
    setSaving(true);
    setServiceModalError(null);
    setFailure(null);
    setBanner(null);
    try {
      const body = {
        code: serviceForm.code.trim(),
        appointmentTypeId: serviceVariantsEnabled ? serviceForm.appointmentTypeId || undefined : undefined,
        name: serviceForm.name.trim(),
        description: serviceForm.description?.trim() || undefined,
        category: serviceForm.category?.trim() || undefined,
        price: Number(serviceForm.price || 0),
        stockRecipe: normalizeServiceRecipe(serviceForm.stockRecipe),
        stockDeductionMode: serviceForm.stockDeductionMode || 'AUTO_LOAD',
        active: serviceForm.active ?? true,
        bookingLink: serviceForm.bookingLink?.trim() || undefined,
        bufferBeforeMinutes: Number(serviceForm.bufferBeforeMinutes || 0),
        bufferAfterMinutes: Number(serviceForm.bufferAfterMinutes || 0),
        iscCode: serviceForm.iscCode?.trim() || undefined,
        onlineBookable: serviceForm.onlineBookable ?? true,
        existingClientsOnly: serviceForm.existingClientsOnly ?? false,
        taxRate: Number(serviceForm.taxRate || 0),
        extraTimeMinutes: Number(serviceForm.extraTimeMinutes || 0),
        serviceChargeAmount: Number(serviceForm.serviceChargeAmount || 0),
        serviceGroup: serviceForm.serviceGroup?.trim() || undefined,
        bundleName: serviceForm.bundleName?.trim() || undefined,
        packageWidgetEnabled: serviceForm.packageWidgetEnabled ?? false,
      };
      const isEdit = Boolean(serviceForm.id);
      const path = isEdit ? `/billing/catalog/services/${serviceForm.id}` : '/billing/catalog/services';
      const res = await fetchApi(path, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      closeServiceModal();
      setBanner(isEdit ? 'Service updated.' : 'Service created.');
      await loadCatalog();
    } catch (err: any) {
      setServiceModalError(err.message || 'Failed to save service.');
    } finally {
      setSaving(false);
    }
  }

  async function copyServiceToClinic(row: BillingServiceRow, openAfterCopy = false) {
    const existingCopy = findClinicServiceCopy(row);
    if (existingCopy) {
      setBanner(`${existingCopy.name} is already in the clinic catalog.`);
      if (openAfterCopy) openServiceModal(existingCopy);
      return existingCopy;
    }

    setSaving(true);
    setFailure(null);
    setBanner(null);
    try {
      const response = await fetchApi('/billing/catalog/services', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: row.code,
          appointmentTypeId: serviceVariantsEnabled ? row.appointmentTypeId || undefined : undefined,
          name: row.name,
          description: row.description || undefined,
          category: row.category || undefined,
          price: Number(row.price || 0),
          stockRecipe: normalizeServiceRecipe(row.stockRecipe),
          active: row.active,
        }),
      });
      if (!response.ok) throw new Error(`Copy failed (${response.status})`);
      const created = (await response.json()) as BillingServiceRow;
      setBanner(`${row.name} copied to clinic catalog. You can edit the clinic copy now.`);
      await loadCatalog();
      if (openAfterCopy) openServiceModal(created);
      return created;
    } catch (err: any) {
      setFailure(err.message || 'Failed to copy service.');
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function openEditableService(row: BillingServiceRow) {
    if (row.scope === 'organization') {
      openServiceModal(row);
      return;
    }
    await copyServiceToClinic(row, true);
  }

  async function deactivateItem(kind: 'services' | 'products', row: BillingServiceRow | BillingProductRow) {
    if (!window.confirm(`Disable ${row.name}?`)) return;
    try {
      const res = await fetchApi(`/billing/catalog/${kind}/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setBanner(`${row.name} disabled.`);
      await loadCatalog();
    } catch (err: any) {
      setFailure(err.message || 'Failed to disable item.');
    }
  }

  async function importStarterTemplates() {
    if (!window.confirm('Import starter service/procedure templates?')) return;
    setSaving(true);
    setFailure(null);
    setBanner(null);
    try {
      for (const svc of starterServices) {
        await fetchApi('/billing/catalog/services', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(svc),
        });
      }
      setBanner('Starter templates imported.');
      await loadCatalog();
    } catch (err: any) {
      setFailure(err.message || 'Failed to import templates.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{tb('Billables Catalog')}</h1>
            <p className="text-slate-500 text-sm">
              {tb('Configure procedures and services used by invoices across your clinic. Products are managed in Inventory.')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <a
            href="/samples/billing-services-procedures-sample.csv"
            download
            className="px-3 py-2 border border-slate-300 bg-white rounded-lg text-sm hover:bg-slate-50 inline-flex items-center gap-1"
          >
            <Download size={14} /> Services CSV
          </a>
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImportCsvFileSelected} />
          <button
            onClick={() => csvInputRef.current?.click()}
            disabled={saving}
            className="px-3 py-2 border border-slate-300 bg-white rounded-lg text-sm hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-1"
          >
            <Upload size={14} /> Import CSV
          </button>
          <button
            onClick={importStarterTemplates}
            disabled={saving}
            className="px-3 py-2 border border-slate-300 bg-white rounded-lg text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            Import Starter Templates
          </button>
        </div>
      </div>

      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        CSV headers supported: <code>code,name,description,category,price,active</code>.
      </div>

      {banner && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-lg">
          <CheckCircle2 size={16} /> {banner}
        </div>
      )}
      {failure && (
        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 px-3 py-2 rounded-lg">
          <ShieldAlert size={16} /> {failure}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MetricCard title="Active Procedures/Services" value={String(services.filter((s) => s.active).length)} icon={<ClipboardList size={18} />} />
        <MetricCard
          title="Average Service Fee"
          value={
            services.length
              ? `$${(services.reduce((sum, s) => sum + Number(s.price || 0), 0) / Math.max(1, services.length)).toFixed(2)}`
              : '$0.00'
          }
          icon={<ClipboardList size={18} />}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <CatalogControlCard title="Categories & groups" icon={<Tags size={18} />}>
          <div className="space-y-2">
            {serviceCategories.length ? serviceCategories.map((category) => (
              <div key={category.name} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                <span className="font-medium text-ink">{category.name}</span>
                <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-500">
                  {category.count} {category.count === 1 ? 'service' : 'services'}
                </span>
              </div>
            )) : (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                Saved service categories appear here after you assign categories to services.
              </div>
            )}
            <p className="text-xs text-slate-500">
              Categories grow organically from saved service categories. Add a new category or reuse an existing one when editing a service.
            </p>
          </div>
        </CatalogControlCard>

        <CatalogControlCard title="Online booking controls" icon={<Link size={18} />}>
          <label className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
            <span>Bulk online-bookable toggle</span>
            <input type="checkbox" checked={bulkOnlineBookable} onChange={(event) => setBulkOnlineBookable(event.target.checked)} className="accent-ink" />
          </label>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div className="rounded-lg border border-slate-100 bg-white p-3">Existing clients only</div>
            <div className="rounded-lg border border-slate-100 bg-white p-3">Custom booking links</div>
            <div className="rounded-lg border border-slate-100 bg-white p-3">Service-code POS search</div>
            <div className="rounded-lg border border-slate-100 bg-white p-3">Alphabetical reorder</div>
          </div>
        </CatalogControlCard>

        <CatalogControlCard title="Packages & service charges" icon={<Package size={18} />}>
          <label className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
            <span>POS service charges</span>
            <input type="checkbox" checked={serviceChargeEnabled} onChange={(event) => setServiceChargeEnabled(event.target.checked)} className="accent-ink" />
          </label>
          <div className="space-y-2">
            {packages.map((pkg, index) => (
              <div key={pkg.name} className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-ink">{pkg.name}</span>
                  <span>${pkg.price}</span>
                </div>
                <p className="text-xs text-slate-500">{pkg.sessions} sessions · {pkg.onlineWidget ? 'Online widget enabled' : 'Admin only'}</p>
                <button type="button" className="mt-2 text-xs text-slate-500 hover:text-ink" onClick={() => setPackages((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, onlineWidget: !item.onlineWidget } : item))}>
                  Toggle online widget
                </button>
              </div>
            ))}
          </div>
        </CatalogControlCard>
      </div>

      <div className="card">
        <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3 items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-ink">Services & Procedures</h2>
            <p className="text-xs text-slate-500">Click a row to edit billing details and associated inventory products.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input
                className="pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg min-w-[260px]"
                placeholder="Search code, description, category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <label className="text-xs text-slate-600 flex items-center gap-1">
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              Show inactive
            </label>
            <button onClick={() => openServiceModal()} className="px-3 py-2 bg-ink text-white rounded-lg text-sm flex items-center gap-1">
              <Plus size={14} /> Add Service
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> {tb('Loading catalog...')}
          </div>
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium">
              <tr>
                <th className="p-4">Code</th>
                {serviceVariantsEnabled ? <th className="p-4">Appointment Type</th> : null}
                <th className="p-4">Name</th>
                <th className="p-4">Category</th>
                <th className="p-4">Price</th>
                <th className="p-4">Scope</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredServices.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => void openEditableService(row)}
                  title={row.scope === 'organization' ? 'Edit service' : 'Create an editable clinic copy'}
                >
                  <td className="p-4 font-medium text-ink">{row.code}</td>
                  {serviceVariantsEnabled ? <td className="p-4 text-slate-600">{row.appointmentTypeName || 'Unassigned'}</td> : null}
                  <td className="p-4">
                    <div className="font-medium text-slate-800">{row.name}</div>
                    <div className="text-xs text-slate-500">{row.description || 'No description'}</div>
                  </td>
                  <td className="p-4 text-slate-600">{row.category || 'General'}</td>
                  <td className="p-4 text-slate-700">${Number(row.price).toFixed(2)}</td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${row.scope === 'organization' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-700'}`}>
                      {row.scope === 'organization' ? 'Organization' : 'System'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${row.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {row.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        className="p-1 text-slate-500 hover:text-ink disabled:opacity-40"
                        onClick={(event) => {
                          event.stopPropagation();
                          void openEditableService(row);
                        }}
                        disabled={saving}
                        title={row.scope === 'organization' ? 'Edit' : 'Create an editable clinic copy'}
                      >
                        <Pencil size={16} />
                      </button>
                      {row.scope !== 'organization' ? (
                        <button
                          className="p-1 text-slate-500 hover:text-ink disabled:opacity-40"
                          onClick={(event) => {
                            event.stopPropagation();
                            void copyServiceToClinic(row, true);
                          }}
                          disabled={saving}
                          aria-label={`Create editable clinic copy of ${row.name}`}
                          title="Create an editable clinic copy"
                        >
                          <CopyPlus size={16} />
                        </button>
                      ) : null}
                      <button
                        className="p-1 text-slate-500 hover:text-rose-600 disabled:opacity-40"
                        onClick={(event) => {
                          event.stopPropagation();
                          void deactivateItem('services', row);
                        }}
                        disabled={row.scope !== 'organization'}
                        title={row.scope !== 'organization' ? 'System templates are read-only' : 'Disable'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredServices.length && (
                <tr>
                  <td colSpan={serviceVariantsEnabled ? 8 : 7} className="p-6 text-center text-slate-500">
                    No services/procedures found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showServiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">{serviceForm.id ? 'Edit Service/Procedure' : 'Add Service/Procedure'}</h2>
              <button onClick={closeServiceModal} className="text-slate-500 hover:text-ink">
                Close
              </button>
            </div>
            <form className="space-y-4" onSubmit={saveService}>
              {serviceModalError ? (
                <div className="flex items-center gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <ShieldAlert size={16} /> {serviceModalError}
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Code">
                  <input className="w-full border rounded px-3 py-2" value={serviceForm.code || ''} onChange={(e) => setServiceForm((s) => ({ ...s, code: e.target.value }))} required />
                </Field>
                {serviceVariantsEnabled ? (
                  <Field label="Appointment type">
                    <select className="w-full border rounded px-3 py-2" value={serviceForm.appointmentTypeId || ''} onChange={(e) => setServiceForm((s) => ({ ...s, appointmentTypeId: e.target.value }))}>
                      <option value="">Unassigned</option>
                      {appointmentTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                <Field label="Category">
                  <SuggestionInput
                    value={serviceForm.category || ''}
                    onChange={(value) => setServiceForm((s) => ({ ...s, category: value }))}
                    options={serviceCategoryOptions}
                    placeholder="Search or add a category"
                    emptyMessage="No saved categories yet. Type to add the first one."
                    ariaLabel="Service category"
                  />
                </Field>
                <Field label="Name">
                  <input className="w-full border rounded px-3 py-2" value={serviceForm.name || ''} onChange={(e) => setServiceForm((s) => ({ ...s, name: e.target.value }))} required />
                </Field>
                <Field label="Base Price">
                  <input type="number" min={0} step="0.01" className="w-full border rounded px-3 py-2" value={serviceForm.price ?? 0} onChange={(e) => setServiceForm((s) => ({ ...s, price: Number(e.target.value) }))} />
                </Field>
                <Field label="Custom booking link">
                  <input className="w-full border rounded px-3 py-2" value={serviceForm.bookingLink || ''} onChange={(e) => setServiceForm((s) => ({ ...s, bookingLink: e.target.value }))} placeholder="https://booking.example.com/service" />
                </Field>
                <Field label="ISC / service code">
                  <input className="w-full border rounded px-3 py-2" value={serviceForm.iscCode || ''} onChange={(e) => setServiceForm((s) => ({ ...s, iscCode: e.target.value }))} placeholder="ISC-001" />
                </Field>
                <Field label="Tax rate (%)">
                  <input type="number" min={0} step="0.01" className="w-full border rounded px-3 py-2" value={serviceForm.taxRate ?? 0} onChange={(e) => setServiceForm((s) => ({ ...s, taxRate: Number(e.target.value) }))} />
                </Field>
                <Field label="Buffer before (min)">
                  <input type="number" min={0} className="w-full border rounded px-3 py-2" value={serviceForm.bufferBeforeMinutes ?? 0} onChange={(e) => setServiceForm((s) => ({ ...s, bufferBeforeMinutes: Number(e.target.value) }))} />
                </Field>
                <Field label="Buffer after (min)">
                  <input type="number" min={0} className="w-full border rounded px-3 py-2" value={serviceForm.bufferAfterMinutes ?? 0} onChange={(e) => setServiceForm((s) => ({ ...s, bufferAfterMinutes: Number(e.target.value) }))} />
                </Field>
                <Field label="Extra time (min)">
                  <input type="number" min={0} className="w-full border rounded px-3 py-2" value={serviceForm.extraTimeMinutes ?? 0} onChange={(e) => setServiceForm((s) => ({ ...s, extraTimeMinutes: Number(e.target.value) }))} />
                </Field>
                <Field label="Service charge">
                  <input type="number" min={0} step="0.01" className="w-full border rounded px-3 py-2" value={serviceForm.serviceChargeAmount ?? 0} onChange={(e) => setServiceForm((s) => ({ ...s, serviceChargeAmount: Number(e.target.value) }))} />
                </Field>
                <Field label="Service group">
                  <input className="w-full border rounded px-3 py-2" value={serviceForm.serviceGroup || ''} onChange={(e) => setServiceForm((s) => ({ ...s, serviceGroup: e.target.value }))} placeholder="Injectables, Skin, Wellness" />
                </Field>
                <Field label="Bundle / package">
                  <input className="w-full border rounded px-3 py-2" value={serviceForm.bundleName || ''} onChange={(e) => setServiceForm((s) => ({ ...s, bundleName: e.target.value }))} placeholder="Consultation bundle" />
                </Field>
              </div>
              <div className="grid grid-cols-1 gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm sm:grid-cols-3">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={serviceForm.onlineBookable ?? true} onChange={(e) => setServiceForm((s) => ({ ...s, onlineBookable: e.target.checked }))} className="accent-ink" />
                  Online bookable
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={serviceForm.existingClientsOnly ?? false} onChange={(e) => setServiceForm((s) => ({ ...s, existingClientsOnly: e.target.checked }))} className="accent-ink" />
                  Existing clients only
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={serviceForm.packageWidgetEnabled ?? false} onChange={(e) => setServiceForm((s) => ({ ...s, packageWidgetEnabled: e.target.checked }))} className="accent-ink" />
                  Online package widget
                </label>
              </div>
              <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-3 md:grid-cols-3">
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <div className="mb-1 flex items-center gap-2 font-medium text-ink"><Percent size={14} /> Pricing matrix</div>
                  <p className="text-xs text-slate-500">Staff, member, and location pricing can be layered from this service.</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <div className="mb-1 flex items-center gap-2 font-medium text-ink"><Clock size={14} /> Buffer and extra time</div>
                  <p className="text-xs text-slate-500">Buffers protect provider calendars; extra time is visible to staff.</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <div className="mb-1 flex items-center gap-2 font-medium text-ink"><ArrowUpDown size={14} /> Reorder rules</div>
                  <p className="text-xs text-slate-500">Use category controls to reorder manually or alphabetically.</p>
                </div>
              </div>
              <Field label="Description">
                <textarea className="w-full border rounded px-3 py-2 h-20 resize-none" value={serviceForm.description || ''} onChange={(e) => setServiceForm((s) => ({ ...s, description: e.target.value }))} />
              </Field>
              <div className="rounded-lg border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
                  <div>
                    <h3 className="text-sm font-semibold text-ink">Associated products</h3>
                    <p className="text-xs text-slate-500">Products come from Inventory. Choose whether they auto-load in the visit or stay available to load on demand.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addServiceRecipeRow}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <Plus size={14} /> Add product
                  </button>
                </div>
                {serviceRecipeError ? (
                  <div className="border-b border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={16} /> {serviceRecipeError}
                    </div>
                  </div>
                ) : null}
                <div className="border-b border-slate-100 bg-slate-50/70 p-4">
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-slate-700">Visit stock loading</span>
                    <select
                      className="w-full rounded border border-slate-300 px-3 py-2"
                      value={serviceForm.stockDeductionMode || 'AUTO_LOAD'}
                      onChange={(event) => setServiceForm((current) => ({ ...current, stockDeductionMode: event.target.value as StockDeductionMode }))}
                    >
                      <option value="AUTO_LOAD">Auto-load linked products when this service is added in the visit</option>
                      <option value="MANUAL_LOAD">Keep linked products available, but let staff load them manually in the visit</option>
                    </select>
                    <p className="mt-1 text-xs text-slate-500">Both modes stay editable in the visit screen. Manual mode simply prevents automatic prefill.</p>
                  </label>
                </div>
                {serviceRecipeRows.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left text-xs">
                      <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="w-[36%] p-2">Product</th>
                          <th className="w-[24%] p-2">Lot preference</th>
                          <th className="w-[76px] p-2">Qty</th>
                          <th className="w-[110px] p-2">Unit</th>
                          <th className="p-2">Notes</th>
                          <th className="w-[44px] p-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {serviceRecipeRows.map((row, index) => {
                          const selectedItem = inventoryItems.find((item) => item.id === row.itemId);
                          const unitOptions = (inventoryItemUnits[row.itemId] || []).map((unit) => ({
                            value: unit.unitId,
                            label: unit.abbreviation || unit.name,
                            description: unit.factorToBaseUnit === 1 ? 'Base unit' : `${unit.factorToBaseUnit} x base unit`,
                          }));
                          const lotOptions = [
                            { value: '', label: 'Auto-select oldest available lot during visit', description: selectedItem?.trackLots ? 'Uses available stock automatically if no lot is pinned here.' : 'This item does not track lots.' },
                            ...lotOptionsForItem(row.itemId),
                          ];
                          return (
                          <tr key={`${row.itemId}-${index}`}>
                            <td className="p-2">
                              <select
                                value={row.itemId}
                                onChange={(event) => selectRecipeInventoryProduct(index, event.target.value)}
                                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs"
                                aria-label={`Service product row ${index + 1}`}
                              >
                                <option value="">Choose inventory product</option>
                                {inventoryItems.map((item) => (
                                  <option key={item.id} value={item.id}>{inventoryLabel(item)}</option>
                                ))}
                              </select>
                            </td>
                            <td className="p-2">
                              <select
                                value={row.lotId || ''}
                                onChange={(event) => selectRecipeLot(index, event.target.value)}
                                disabled={!row.itemId}
                                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs disabled:bg-slate-50"
                                aria-label={`Lot preference row ${index + 1}`}
                              >
                                {lotOptions.map((option) => (
                                  <option key={option.value || 'auto'} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                min={0.01}
                                step={0.01}
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
                                value={row.quantity}
                                onChange={(event) => updateServiceRecipe(index, { quantity: Number(event.target.value) || 1 })}
                              />
                            </td>
                            <td className="p-2">
                              <select
                                value={row.unitId || ''}
                                onChange={(event) => selectRecipeUnit(index, event.target.value)}
                                disabled={!row.itemId || !unitOptions.length}
                                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs disabled:bg-slate-50"
                                aria-label={`Unit row ${index + 1}`}
                              >
                                <option value="">{row.itemId ? 'Choose unit' : 'Select product first'}</option>
                                {unitOptions.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="p-2">
                              <input
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
                                value={row.notes || ''}
                                onChange={(event) => updateServiceRecipe(index, { notes: event.target.value })}
                                placeholder="Note"
                              />
                            </td>
                            <td className="p-2 text-right">
                              <button
                                type="button"
                                onClick={() => removeServiceRecipeRow(index)}
                                className="p-1 text-slate-500 hover:text-rose-600"
                                aria-label="Remove associated product"
                                title="Remove associated product"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
                      Choose the inventory product once. Lot preference is optional; if you leave it on auto, the visit flow will use the oldest available lot automatically. Units come from that product&apos;s allowed consumption conversions.
                    </div>
                  </div>
                ) : (
                  <div className="p-4 text-sm text-slate-500">No associated products yet.</div>
                )}
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={serviceForm.active ?? true} onChange={(e) => setServiceForm((s) => ({ ...s, active: e.target.checked }))} />
                Active
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeServiceModal} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-ink text-white rounded disabled:opacity-60">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ title, value, icon }: { title: string; value: string; icon: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between text-slate-500">
        <span className="text-sm">{title}</span>
        {icon}
      </div>
      <div className="text-2xl font-semibold text-ink mt-2">{value}</div>
    </div>
  );
}

function CatalogControlCard({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-ink">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm space-y-1">
      <span className="text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function SuggestionInput({ value, onChange, options, placeholder, emptyMessage, ariaLabel }: { value: string; onChange: (value: string) => void; options: string[]; placeholder?: string; emptyMessage?: string; ariaLabel: string }) {
  const [open, setOpen] = useState(false);
  const trimmedValue = value.trim();
  const normalizedValue = trimmedValue.toLowerCase();
  const filteredOptions = useMemo(() => {
    const seen = new Set<string>();
    return options
      .map((option) => option.trim())
      .filter((option) => {
        if (!option) return false;
        const normalizedOption = option.toLowerCase();
        if (seen.has(normalizedOption)) return false;
        seen.add(normalizedOption);
        return !normalizedValue || normalizedOption.includes(normalizedValue);
      })
      .slice(0, 8);
  }, [normalizedValue, options]);
  const exactMatch = options.some((option) => option.trim().toLowerCase() === normalizedValue);
  const showCreateOption = Boolean(trimmedValue) && !exactMatch;

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        className="w-full rounded border border-slate-300 px-3 py-2 pr-10 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
      />
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((current) => !current)}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 transition hover:text-slate-600"
        aria-label={`Toggle ${ariaLabel.toLowerCase()} suggestions`}
      >
        <ChevronDown size={16} className={open ? 'rotate-180' : ''} />
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl ring-1 ring-slate-100">
          {filteredOptions.map((option) => (
            <button
              key={option}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className="block w-full border-b border-slate-100 px-3 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50 last:border-b-0"
            >
              {option}
            </button>
          ))}
          {showCreateOption ? (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(trimmedValue);
                setOpen(false);
              }}
              className="block w-full border-t border-slate-100 bg-slate-50 px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Add &quot;{trimmedValue}&quot;
            </button>
          ) : null}
          {!filteredOptions.length && !showCreateOption ? (
            <div className="px-3 py-2.5 text-sm text-slate-500">{emptyMessage || 'No saved options yet.'}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
